import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PlusIcon,
  FolderIcon,
  TrashIcon,
  ChartBarIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { listDiscoveryCases, createDiscoveryCase, deleteDiscoveryCase } from '../services/api';

export default function CaseList() {
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newCase, setNewCase] = useState({
    case_name: '',
    case_number: '',
    custodians: '',
    search_terms: '',
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    loadCases();
  }, []);

  const loadCases = async () => {
    setLoading(true);
    try {
      const res = await listDiscoveryCases();
      setCases(res.cases || []);
    } catch (err) {
      console.error('Failed to load cases:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newCase.case_name.trim()) return;
    setCreating(true);
    try {
      const res = await createDiscoveryCase({
        case_name: newCase.case_name,
        case_number: newCase.case_number,
        custodians: newCase.custodians.split(',').map(s => s.trim()).filter(Boolean),
        search_terms: newCase.search_terms.split(',').map(s => s.trim()).filter(Boolean),
      });
      setShowCreate(false);
      setNewCase({ case_name: '', case_number: '', custodians: '', search_terms: '' });
      navigate(`/cases/${res.case.case_id}`);
    } catch (err) {
      console.error('Failed to create case:', err);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (caseId, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this case and all its documents?')) return;
    try {
      await deleteDiscoveryCase(caseId);
      setCases(prev => prev.filter(c => c.case_id !== caseId));
    } catch (err) {
      console.error('Failed to delete case:', err);
    }
  };

  return (
    <div className="max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Discovery Cases</h1>
          <p className="text-sm text-gray-500">Manage legal discovery cases, document review, and production</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center gap-1.5"
        >
          <PlusIcon className="w-4 h-4" />
          New Case
        </button>
      </div>

      {/* Create case form */}
      {showCreate && (
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Create New Case</h2>
          <form onSubmit={handleCreate} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Case Name *</label>
                <input
                  type="text"
                  value={newCase.case_name}
                  onChange={e => setNewCase(prev => ({ ...prev, case_name: e.target.value }))}
                  placeholder="e.g., Smith v. Acme Corp"
                  className="w-full text-sm border border-gray-300 rounded-md px-3 py-2"
                  required
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Case Number</label>
                <input
                  type="text"
                  value={newCase.case_number}
                  onChange={e => setNewCase(prev => ({ ...prev, case_number: e.target.value }))}
                  placeholder="e.g., 2026-CV-1234"
                  className="w-full text-sm border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Custodians (comma-separated)</label>
              <input
                type="text"
                value={newCase.custodians}
                onChange={e => setNewCase(prev => ({ ...prev, custodians: e.target.value }))}
                placeholder="e.g., John Smith, Jane Doe, Bob Wilson"
                className="w-full text-sm border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Search Terms (comma-separated)</label>
              <input
                type="text"
                value={newCase.search_terms}
                onChange={e => setNewCase(prev => ({ ...prev, search_terms: e.target.value }))}
                placeholder="e.g., product liability, safety testing, defect"
                className="w-full text-sm border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <button
                type="submit"
                disabled={creating}
                className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
              >
                {creating ? 'Creating...' : 'Create Case'}
              </button>
              <button
                type="button"
                onClick={() => setShowCreate(false)}
                className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Case list */}
      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin h-6 w-6 border-4 border-indigo-500 border-t-transparent rounded-full" />
        </div>
      ) : cases.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <FolderIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 mb-2">No discovery cases yet.</p>
          <button
            onClick={() => setShowCreate(true)}
            className="text-indigo-600 hover:underline text-sm"
          >
            Create your first case
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {cases.map(c => (
            <div
              key={c.case_id}
              onClick={() => navigate(`/cases/${c.case_id}`)}
              className="bg-white rounded-lg border border-gray-200 p-4 hover:border-indigo-300 hover:shadow-sm cursor-pointer transition-all"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FolderIcon className="w-8 h-8 text-indigo-400" />
                  <div>
                    <h3 className="text-base font-semibold text-gray-900">{c.case_name}</h3>
                    <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                      {c.case_number && <span>#{c.case_number}</span>}
                      <span className={`px-1.5 py-0.5 rounded-full ${
                        c.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {c.status}
                      </span>
                      <span className="flex items-center gap-1">
                        <ClockIcon className="w-3 h-3" />
                        {c.created_at ? new Date(c.created_at).toLocaleDateString() : ''}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <div className="text-lg font-bold text-gray-700">{c.total_documents || 0}</div>
                    <div className="text-xs text-gray-500">documents</div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-blue-600">{c.reviewed_documents || 0}</div>
                    <div className="text-xs text-gray-500">reviewed</div>
                  </div>
                  <button
                    onClick={(e) => handleDelete(c.case_id, e)}
                    className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                    title="Delete case"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
